import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { FileWithPreview } from 'LibNeo'
import { ResponseCatalog, RequestParams, ReceivingFileModel } from 'Models'
import {
	ExternalFormModel,
	ExternalFormShortInfoModel,
	ProductModel,
	markExternalFormModel,
} from 'Pages/orders'
import { OrderModel } from 'Pages/orders_design'
import { Callback } from 'Src/types/utilTypes'

type ExternalFormState = {
	externalForms: ResponseCatalog<ExternalFormModel>
	externalForm: ExternalFormModel
	requestParams: RequestParams
	order: OrderModel
	products: ProductModel[]
	regionIp: number
	instruction: ReceivingFileModel
	with_instruction: boolean
}

const initialExternalFormState: ExternalFormState = {
	externalForms: new ResponseCatalog<ExternalFormModel>(),
	externalForm: null,
	requestParams: new RequestParams(),
	order: null,
	products: [],
	regionIp: null,
	instruction: null,
	with_instruction: false,
}

export namespace ExternalFormNS {
	export type GetAll = PayloadAction<{
		params: RequestParams
		clear?: boolean
	}>
	export type SetAll = PayloadAction<ResponseCatalog<ExternalFormModel>>
	export type GetOne = PayloadAction<number>
	export type SetOne = PayloadAction<ExternalFormModel>
	export type ClearOne = PayloadAction
	export type UpdateOne = PayloadAction<{
		externalForm: ExternalFormShortInfoModel
		id: string
	}>
	export type CreateOne = PayloadAction<ExternalFormShortInfoModel>
	export type SetRequestParams = PayloadAction<RequestParams>
	export type DeleteOne = PayloadAction<ExternalFormModel>
	export type RestoreOne = PayloadAction<ExternalFormModel>
	export type GetOrderBuild = PayloadAction<string>
	export type SetOrderBuild = PayloadAction<OrderModel>
	export type CreateOrderBuild = PayloadAction<{
		token: string
		data: any
		callback: Callback
		showMessage?: boolean
		errorCallback?: Callback
	}>
	export type SendPostback = PayloadAction<{
		clickId: string
		trackId: string
		partnerInn: string
	}>
	export type GetProducts = PayloadAction<string>
	export type SetProducts = PayloadAction<ProductModel[]>
	export type MarkExternalForm = PayloadAction<{
		data: markExternalFormModel
		token: string
		callback: Callback
	}>
	export type AdMarkingState = PayloadAction<{
		id: number
		advertisement: boolean
		ad_marking_state: string
		landing_url_with_marker: string
	}>
	export type SendReport = PayloadAction<{
		params: RequestParams
	}>
	export type GetRegionIp = PayloadAction
	export type SetRegionIp = PayloadAction<number>
	export type UploadInstructionFile = PayloadAction<{
		file: FileWithPreview | FileWithPreview[]
		callback?: Callback
	}>
	export type GetInstructionFile = PayloadAction
	export type SetInstructionFile = PayloadAction<ReceivingFileModel>
	export type GetWithInstructionFlag = PayloadAction<{
		params: RequestParams
	}>
	export type SetWithInstructionFlag = PayloadAction<boolean>
	export type SetOneUpdate = PayloadAction<ExternalFormModel>
}

export const externalFormSlice = createSlice({
	name: 'externalForms',
	initialState: initialExternalFormState,
	reducers: {
		getAll: (state, action: ExternalFormNS.GetAll) => {},
		setAll: (state, action: ExternalFormNS.SetAll) => {
			state.externalForms = action.payload
		},
		setRequestParams: (state, action: ExternalFormNS.SetRequestParams) => {
			state.requestParams = action.payload
		},
		clearAll: (state) => {
			state.externalForms = new ResponseCatalog<ExternalFormModel>()
			state.requestParams = new RequestParams()
		},
		setOneUpdate: (state, action: ExternalFormNS.SetOneUpdate) => {
			const { id } = action.payload
			state.externalForms.entries = state.externalForms.entries.map(
				(item: ExternalFormModel): ExternalFormModel => {
					if (item.id === id) {
						return action.payload
					}
					return item
				}
			)
		},
		getOne: (state, action: ExternalFormNS.GetOne) => {},
		setOne: (state, action: ExternalFormNS.SetOne) => {
			state.externalForm = action.payload
		},
		clearOne: (state, action: ExternalFormNS.ClearOne) => {
			state.externalForm = null
		},
		updateOne: (state, action: ExternalFormNS.UpdateOne) => {},
		createOne: (state, action: ExternalFormNS.CreateOne) => {},
		restoreOne: (state, action: ExternalFormNS.RestoreOne) => {},
		deleteOne: (state, action: ExternalFormNS.DeleteOne) => {},

		getOrderBuild: (state, action: ExternalFormNS.GetOrderBuild) => {},
		setOrderBuild: (state, action: ExternalFormNS.SetOrderBuild) => {
			state.order = action.payload
		},

		getProducts: (state, action: ExternalFormNS.GetProducts) => {},
		setProducts: (state, action: ExternalFormNS.SetProducts) => {
			state.products = action.payload
		},
		createOrderBuild: (state, action: ExternalFormNS.CreateOrderBuild) => {},
		sendPostback: (state, action: ExternalFormNS.SendPostback) => {},

		adMarkingState: (state, action: ExternalFormNS.AdMarkingState) => {},
		markExternalForm: (state, action: ExternalFormNS.MarkExternalForm) => {},
		sendReport: (state, action: ExternalFormNS.SendReport) => {},
		getRegionIp: (state, action: ExternalFormNS.GetRegionIp) => {},
		setRegionIp: (state, action: ExternalFormNS.SetRegionIp) => {
			state.regionIp = action.payload
		},
		uploadInstructionFile: (state, action: ExternalFormNS.UploadInstructionFile) => {},
		getInstructionFile: (state, action: ExternalFormNS.GetInstructionFile) => {},
		setInstructionFile: (state, action: ExternalFormNS.SetInstructionFile) => {
			state.instruction = action.payload
		},
		getWithInstructionFlag: (state, action: ExternalFormNS.GetWithInstructionFlag) => {},
		setWithInstructionFlag: (state, action: ExternalFormNS.SetWithInstructionFlag) => {
			state.with_instruction = action.payload
		},
	},
})
